import { useState, useRef, useEffect, useMemo } from "react";
import { Search, ChevronDown, X } from "lucide-react";

/**
 * Lightweight searchable dropdown.
 * Props:
 *   value: string
 *   onValueChange: (v: string) => void
 *   options: string[]
 *   placeholder?: string
 *   allowClear?: boolean (default true)
 *   className?: string
 *   testId?: string
 */
export default function SearchableSelect({
  value = "",
  onValueChange,
  options = [],
  placeholder = "Select...",
  allowClear = true,
  className = "",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 20);
    } else {
      setQ("");
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!q.trim()) return options;
    const qq = q.toLowerCase().trim();
    return options.filter((o) => o.toLowerCase().includes(qq));
  }, [options, q]);

  const select = (opt) => {
    onValueChange(opt);
    setOpen(false);
  };
  const clear = (e) => {
    e.stopPropagation();
    onValueChange("");
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-9 px-3 pr-8 flex items-center justify-between rounded-md border border-[#E6E6E6] bg-white text-sm hover:border-[#074572]/40 focus:outline-none focus:border-[#074572]/60 transition-colors"
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className={value ? "text-zinc-800 truncate" : "text-zinc-400"}>{value || placeholder}</span>
        {value && allowClear ? (
          <span
            role="button"
            tabIndex={0}
            onClick={clear}
            onKeyDown={(e) => { if (e.key === "Enter") clear(e); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-100 text-zinc-400"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <X size={12} />
          </span>
        ) : (
          <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#E6E6E6] rounded-md shadow-lg overflow-hidden">
          <div className="relative border-b border-[#E6E6E6]">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type to search..."
              className="w-full h-9 pl-8 pr-3 text-sm bg-transparent outline-none"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-xs text-zinc-400 text-center">No matches</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => select(opt)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 ${value === opt ? "bg-[#074572]/5 text-[#074572] font-medium" : "text-zinc-700"}`}
                  data-testid={testId ? `${testId}-option-${opt.toLowerCase().replace(/\s+/g, "-")}` : undefined}
                >
                  {opt}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
